import { Controller } from "@hotwired/stimulus"
import consumer from "../../channels/consumer"

// Stimulus controller for sitemap generation with real-time updates
export default class extends Controller {
  static targets = [
    "statusBadge", "urlCount", "lastGenerated", "errorMessage",
    "regenerateButton", "buttonText", "buttonSpinner", "buttonIcon",
    "deployButton", "deployText", "deploySpinner", "deployIcon",
    "cardContent"
  ]

  static values = {
    websiteId: String,
    regenerateUrl: String
  }

  connect() {
    this.subscribeToChannel()
  }

  disconnect() {
    this.unsubscribeFromChannel()
  }

  subscribeToChannel() {
    if (!this.websiteIdValue) return

    // Subscribe to settings channel for sitemap events
    this.subscription = consumer.subscriptions.create(
      { channel: "WebsiteSettingsChannel", website_id: this.websiteIdValue },
      {
        connected: () => {
          console.log("[Sitemap] Connected to WebsiteSettingsChannel")
        },
        disconnected: () => {
          console.log("[Sitemap] Disconnected from WebsiteSettingsChannel")
        },
        received: (data) => {
          this.handleMessage(data)
        }
      }
    )

    // Subscribe to deployment channel for deploy completion
    this.deploymentSubscription = consumer.subscriptions.create(
      { channel: "DeploymentProgressChannel", website_id: this.websiteIdValue },
      {
        connected: () => {
          console.log("[Sitemap] Connected to DeploymentProgressChannel")
        },
        disconnected: () => {
          console.log("[Sitemap] Disconnected from DeploymentProgressChannel")
        },
        received: (data) => {
          this.handleDeploymentMessage(data)
        }
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    if (this.deploymentSubscription) {
      this.deploymentSubscription.unsubscribe()
      this.deploymentSubscription = null
    }
  }

  regenerate(event) {
    event.preventDefault()
    this.startGeneration(false)
  }

  regenerateAndDeploy(event) {
    event.preventDefault()
    this.startGeneration(true)
  }

  startGeneration(deploy = false) {
    // Show loading state immediately
    this.showGenerating(deploy)
    this.willDeploy = deploy

    // Build URL with deploy param
    const url = new URL(this.regenerateUrlValue, window.location.origin)
    if (deploy) {
      url.searchParams.set('deploy', 'true')
    }

    // Submit via fetch
    fetch(url.toString(), {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log("[Sitemap] Generation started, waiting for ActionCable...")
          const message = deploy ?
            "Generating sitemap and preparing deployment..." :
            "Sitemap generation started..."
          this.showToast(message, "info")
        } else {
          this.showError(data.error || "Failed to start generation")
        }
      })
      .catch((error) => {
        console.error("[Sitemap] Fetch error:", error)
        this.showError("Network error. Please try again.")
      })
  }

  handleMessage(data) {
    console.log("[Sitemap] Received:", data)

    switch (data.event) {
      case "sitemap_generation_started":
        this.showGenerating(this.willDeploy)
        break
      case "sitemap_generation_completed":
        this.showCompleted(data)
        break
      case "sitemap_generation_failed":
        this.showFailed(data.error)
        break
      case "sitemap_deployment_started":
        this.showDeploying()
        break
    }
  }

  handleDeploymentMessage(data) {
    // Only handle deployment events if we triggered the deploy
    if (!this.willDeploy) return

    console.log("[Sitemap] Deployment event:", data)

    switch (data.event) {
      case "deployment_completed":
        this.showDeploymentCompleted()
        break
      case "deployment_failed":
        this.showDeploymentFailed(data.error || data.message)
        break
    }
  }

  showDeploymentCompleted() {
    // Reset to completed state
    this.resetToCompleted({})

    // Update status badge to show deployed
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          Deployed
        </span>
      `
    }

    this.showToast("Sitemap deployed to live site!", "success")
  }

  showDeploymentFailed(error) {
    this.resetToCompleted({})
    this.showToast(`Deployment failed: ${error}`, "error")
  }

  showGenerating(withDeploy = false) {
    // Update status badge
    const statusText = withDeploy ? "Generating & Deploying..." : "Generating..."
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <svg class="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          ${statusText}
        </span>
      `
    }

    // Update regenerate button state
    if (this.hasRegenerateButtonTarget) {
      this.regenerateButtonTarget.disabled = true
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "Generating..."
    }
    if (this.hasButtonSpinnerTarget) {
      this.buttonSpinnerTarget.classList.remove("hidden")
    }
    if (this.hasButtonIconTarget) {
      this.buttonIconTarget.classList.add("hidden")
    }

    // Update deploy button state
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.disabled = true
    }
    if (this.hasDeployTextTarget) {
      this.deployTextTarget.textContent = withDeploy ? "Generating..." : "Regenerate & Deploy"
    }
    if (this.hasDeploySpinnerTarget) {
      if (withDeploy) {
        this.deploySpinnerTarget.classList.remove("hidden")
      } else {
        this.deploySpinnerTarget.classList.add("hidden")
      }
    }
    if (this.hasDeployIconTarget) {
      if (withDeploy) {
        this.deployIconTarget.classList.add("hidden")
      } else {
        this.deployIconTarget.classList.remove("hidden")
      }
    }

    // Hide any previous error
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }

    // Add loading overlay to card content
    if (this.hasCardContentTarget) {
      this.cardContentTarget.classList.add("sitemap-generating")
    }
  }

  showCompleted(data) {
    // If we're deploying, update status to show deployment in progress
    if (this.willDeploy) {
      this.showDeploying()
      return
    }

    this.resetToCompleted(data)
  }

  showDeploying() {
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <svg class="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          Deploying...
        </span>
      `
    }

    if (this.hasDeployTextTarget) {
      this.deployTextTarget.textContent = "Deploying..."
    }

    this.showToast("Sitemap generated. Now deploying to live site...", "info")
  }

  resetToCompleted(data) {
    // Update status badge
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          Generated
        </span>
      `
    }

    // Update URL count
    if (this.hasUrlCountTarget && data && data.url_count !== undefined) {
      this.urlCountTarget.textContent = `${data.url_count} URLs`
    }

    // Update last generated time
    if (this.hasLastGeneratedTarget) {
      this.lastGeneratedTarget.textContent = "Just now"
    }

    // Reset regenerate button state
    if (this.hasRegenerateButtonTarget) {
      this.regenerateButtonTarget.disabled = false
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "Regenerate"
    }
    if (this.hasButtonSpinnerTarget) {
      this.buttonSpinnerTarget.classList.add("hidden")
    }
    if (this.hasButtonIconTarget) {
      this.buttonIconTarget.classList.remove("hidden")
    }

    // Reset deploy button state
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.disabled = false
    }
    if (this.hasDeployTextTarget) {
      this.deployTextTarget.textContent = "Regenerate & Deploy"
    }
    if (this.hasDeploySpinnerTarget) {
      this.deploySpinnerTarget.classList.add("hidden")
    }
    if (this.hasDeployIconTarget) {
      this.deployIconTarget.classList.remove("hidden")
    }

    // Remove loading overlay
    if (this.hasCardContentTarget) {
      this.cardContentTarget.classList.remove("sitemap-generating")
    }

    // Reset deploy flag
    this.willDeploy = false

    // Show success toast
    if (data && data.url_count !== undefined) {
      this.showToast(`Sitemap generated with ${data.url_count} URLs`, "success")
    }
  }

  showFailed(error) {
    // Update status badge
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.innerHTML = `
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          Failed
        </span>
      `
    }

    // Show error message
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = error
      this.errorMessageTarget.classList.remove("hidden")
    }

    // Reset regenerate button state
    if (this.hasRegenerateButtonTarget) {
      this.regenerateButtonTarget.disabled = false
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "Retry"
    }
    if (this.hasButtonSpinnerTarget) {
      this.buttonSpinnerTarget.classList.add("hidden")
    }
    if (this.hasButtonIconTarget) {
      this.buttonIconTarget.classList.remove("hidden")
    }

    // Reset deploy button state
    if (this.hasDeployButtonTarget) {
      this.deployButtonTarget.disabled = false
    }
    if (this.hasDeployTextTarget) {
      this.deployTextTarget.textContent = "Regenerate & Deploy"
    }
    if (this.hasDeploySpinnerTarget) {
      this.deploySpinnerTarget.classList.add("hidden")
    }
    if (this.hasDeployIconTarget) {
      this.deployIconTarget.classList.remove("hidden")
    }

    // Remove loading overlay
    if (this.hasCardContentTarget) {
      this.cardContentTarget.classList.remove("sitemap-generating")
    }

    // Reset deploy flag
    this.willDeploy = false

    // Show error toast
    this.showToast(error || "Sitemap generation failed", "error")
  }

  showError(error) {
    this.showFailed(error)
  }

  showToast(message, type = "info") {
    // Remove any existing toasts
    document.querySelectorAll(".sitemap-toast").forEach((t) => t.remove())

    const toast = document.createElement("div")
    toast.className = `sitemap-toast sitemap-toast-${type}`

    let iconSvg
    switch (type) {
      case "success":
        iconSvg = `<svg class="sitemap-toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
        break
      case "error":
        iconSvg = `<svg class="sitemap-toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
        break
      default:
        iconSvg = `<svg class="sitemap-toast-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
    }

    toast.innerHTML = `
      <div class="sitemap-toast-content">
        ${iconSvg}
        <span>${message}</span>
      </div>
    `

    this.ensureToastStyles()
    document.body.appendChild(toast)

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add("sitemap-toast-show")
    })

    // Auto dismiss
    setTimeout(() => {
      toast.classList.remove("sitemap-toast-show")
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  ensureToastStyles() {
    if (document.getElementById("sitemap-controller-styles")) return

    const style = document.createElement("style")
    style.id = "sitemap-controller-styles"
    style.textContent = `
      .sitemap-toast {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
        transform: translateY(20px);
        opacity: 0;
        transition: transform 0.3s ease, opacity 0.3s ease;
      }
      .sitemap-toast-show {
        transform: translateY(0);
        opacity: 1;
      }
      .sitemap-toast-content {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      }
      .sitemap-toast-success .sitemap-toast-content {
        background: #ecfdf5;
        color: #047857;
        border: 1px solid #a7f3d0;
      }
      .sitemap-toast-error .sitemap-toast-content {
        background: #fef2f2;
        color: #b91c1c;
        border: 1px solid #fecaca;
      }
      .sitemap-toast-info .sitemap-toast-content {
        background: #eff6ff;
        color: #1d4ed8;
        border: 1px solid #bfdbfe;
      }
      .sitemap-toast-icon {
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
      }

      /* Loading state for sitemap card */
      .sitemap-generating {
        position: relative;
        pointer-events: none;
      }
      .sitemap-generating::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(255,255,255,0.6);
        border-radius: inherit;
        z-index: 1;
      }
    `
    document.head.appendChild(style)
  }
}
